<template>
  <div class="alipay-result">
    <div class="result-header">
      <i class="fas fa-chevron-left back-icon" @click="goBack"></i>
      <div class="header-title">支付结果</div>
      <span class="header-placeholder"></span>
    </div>
    <!-- 结果图标 -->
    <div class="result-icon" :class="{ success: isSuccess, fail: !isSuccess }">
      <i :class="isSuccess ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
    </div>

    <!-- 结果标题 -->
    <div class="result-title">{{ isSuccess ? '支付成功' : '等待支付' }}</div>

    <!-- 结果信息 -->
    <div class="result-info">
      <template v-if="isSuccess">
        <div class="amount">¥{{ amount }}</div>
        <div class="order-no">订单号：{{ orderNumber }}</div>
      </template>
      <template v-else>
        <div class="error-message">请尽快完成支付，订单将在15分钟后关闭</div>
      </template>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <template v-if="isSuccess">
        <button class="primary-button" @click="goToOrderDetail">查看订单</button>
        <button class="secondary-button" @click="goToHome">返回首页</button>
      </template>
      <template v-else>
        <button class="primary-button" @click="continuePay">继续支付</button>
        <button class="secondary-button" @click="confirmPaid">已完成支付</button>
      </template>
    </div>
  </div>
  <van-dialog
    title=" "
    v-model:show="displayIosPaymentDialog"
    @confirm="confirmPay"
    show-cancel-button
  >
    <div
      style="display: flex; flex-direction: column; justify-content: center; align-items: center"
    >
      <van-image
        width="100"
        fit="contain"
        src="https://gw.alipayobjects.com/mdn/rms_7e962d/afts/img/A*yCL8TrkjIx8AAAAAAAAAAAAAARQnAQ"
        class="restaurant-image"
        style="text-align: center"
      />
      <div style="text-align: center; line-height: 24px; color: blue; padding: 15px"
        >是否跳转至支付宝页面</div
      ></div
    >
  </van-dialog>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { showToast } from 'vant';
  import h5Config from '@/config/config';
  import { startPayment } from '@/service/payment';

  const route = useRoute();
  const router = useRouter();

  // 状态变量
  const isSuccess = ref(true);
  const amount = ref('0.00');
  const orderNumber = ref('');
  const errorMessage = ref('支付未完成，请重试');
  const payUrl = ref('');
  const displayIosPaymentDialog = ref(false);

  // 设备检测
  const isIOS = computed(() => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  });

  const isWeChatBrowser = computed(() => {
    return /MicroMessenger/i.test(navigator.userAgent);
  });

  onMounted(() => {
    // 从URL获取支付结果参数
    const { status, totalAmount, orderNo } = route.query;

    // 设置支付结果
    isSuccess.value = status === 'TRADE_SUCCESS';

    // 设置订单信息
    if (totalAmount) amount.value = (totalAmount / 100) as string;
    if (orderNo) orderNumber.value = orderNo as string;

    // 设置错误信息
    if (!isSuccess.value) {
      errorMessage.value = (route.query.message as string) || '支付未完成，请重试';
    }
  });

  // 跳转到订单详情
  const goToOrderDetail = () => {
    const orderId = route.query.orderId;
    if (orderId) {
      router.push(`/order/${orderId}`);
    } else {
      router.push('/order');
    }
  };

  const confirmPaid = () => {
    goToOrderDetail();
  };

  // 返回首页
  const goToHome = () => {
    router.push('/');
  };

  // 重新支付
  const continuePay = () => {
    const orderId = route.query.orderId;
    const paymentMethod = route.query.paymentMethod;
    const amount = route.query.totalAmount / 100;
    const orderNo = route.query.orderNo;
    if (orderId) {
      handleStartPayment(orderId, paymentMethod, amount, orderNo);
    } else {
      showToast('订单信息不存在');
    }
  };

  const handleStartPayment = async (orderId, paymentMethod, amount, orderNo) => {
    try {
      const response = await startPayment({
        orderId: orderId,
        paymentMethod: paymentMethod,
        amount: amount,
        openId: 'openId',
        returnUrl:
          window.location.origin +
          '/payment/alipay/result?orderId=' +
          orderId +
          '&orderNo=' +
          orderNo +
          '&paymentMethod=2',
      });
      if (response.code === 200) {
        const paymentUrl = response.result.request;
        const paymentNo = response.result.paymentNo;
        console.log(paymentUrl);
        payUrl.value = paymentUrl;
        if (payUrl.value) {
          // 根据不同设备采用不同支付策略
          if (isIOS.value) {
            displayIosPaymentDialog.value = true;
          } else {
            startNormalPayment(payUrl.value);
          }
        }
      }
    } catch (error) {
      handlePaymentError('支付请求失败，请重试');
    }
  };

  const confirmPay = () => {
    startIOSPayment(payUrl.value);
  };

  // iOS设备支付策略
  const startIOSPayment = (payUrl) => {
    try {
      window.location.href = payUrl;
    } catch (error) {
      console.error('iOS支付启动失败:', error);
      handlePaymentError('支付启动失败，请重试');
    }
  };

  // 普通设备支付策略
  const startNormalPayment = (payUrl) => {
    window.location.href = payUrl;
  };

  const handlePaymentError = (message) => {
    errorMessage.value = message;
  };
</script>

<style scoped lang="less">
  .alipay-result {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    // padding: 40px 20px;
    background-color: #fff;

    .result-header {
      height: 48px;
      background: v-bind('h5Config.themeColor');
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 18px;
      width: 100vw;
      max-width: 500px;

      .back-icon {
        font-size: 22px;
        cursor: pointer;
        margin-left: 16px;
      }

      .header-title {
        flex: 1;
        text-align: center;
        font-weight: 500;
      }

      .header-placeholder {
        width: 22px;
        margin-right: 16px;
      }
    }

    .result-icon {
      font-size: 80px;
      margin-bottom: 20px;
      margin-top: 20px;

      &.success {
        color: #52c41a;
      }

      &.fail {
        color: #ffa500;
      }
    }

    .result-title {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 20px;
    }

    .result-info {
      text-align: center;
      margin-bottom: 40px;

      .amount {
        font-size: 32px;
        font-weight: bold;
        color: #333;
        margin-bottom: 10px;
      }

      .order-no {
        font-size: 14px;
        color: #666;
      }

      .error-message {
        font-size: 16px;
        color: #ff4d4f;
      }
    }

    .action-buttons {
      display: flex;
      gap: 15px;
      width: 100%;
      max-width: 320px;

      button {
        flex: 1;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: opacity 0.2s;

        &:active {
          opacity: 0.8;
        }

        &.primary-button {
          background-color: v-bind('h5Config.themeColor');
          color: white;
          border: none;
        }

        &.secondary-button {
          background-color: #f5f5f5;
          color: #333;
          border: 1px solid #d9d9d9;
        }
      }
    }
  }
</style>
